import React, { useState } from 'react';

import { downloadUrl } from '@apis/common';
import { FormTitle } from '@components/admin/Forms';
import { CompanyCustomerServiceGradesForm } from '@components/admin/members/MemberDetails/CompanyCustomerServiceGradesForm';
import { MemberAgencyDialog } from '@components/admin/members/MemberDetails/MemberAgencyDialog';
import { Button } from '@components/Button';
import { Dialog } from '@components/Dialog';
import { TextInput } from '@components/Forms/TextInput';
import { LoadingArea } from '@components/LoadingArea';
import { SpinnerModal } from '@components/SpinnerModal';
import { agencyStrings } from '@data/agency';
import { companyStrings } from '@data/company';
import { usePatchCompany } from '@hooks/mutation/dashboard/usePatchCompany';
import { usePatchCompanyMemberNo } from '@hooks/mutation/dashboard/usePatchCompanyMemberNo';
import { usePostCompanySalesperson } from '@hooks/mutation/dashboard/usePostCompanySalesperson';
import { usePostK01 } from '@hooks/mutation/dashboard/usePostK01';
import { useCompanyDelegation } from '@hooks/queries/dashboard/useCompanyDelegation';
import { useCompanyDetail } from '@hooks/queries/dashboard/useCompanyDetail';
import { useCompanyMemberNo } from '@hooks/queries/dashboard/useCompanyMemberNo';
import { useRoutePlanList } from '@hooks/queries/dashboard/useRoutePlanList';
import { useK01 } from '@hooks/queries/useK01';
import { useUser } from '@hooks/queries/useUser';
import { useConfirmDialog } from '@hooks/useActionableDialog';
import { allCompanyLevel, allCompanyRole } from '@models/Company';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { FormError } from '@utils/errors';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { formatDateTime } from '@utils/types';
import { Form, Formik } from 'formik';
import { useHistory } from 'react-router-dom';

const strings = createStrings({
  ja: {
    infoManagement: '管理情報',
    edit: '編集',
    id: '申し込み番号',
    createdAt: '申し込み時間',
    status: '対応状況',
    admissionDate: '入会時間',
    completeApplication: '入会手続き完了',
    assignee: '営業担当者',
    becomeAssignee: '担当者になる',
    evaluation: '会員総合評価',
    connectStem: 'FC基幹システムと繋がる',
    export: 'エクスポート',
    createEvaluationSheet: '評価シートを作成',
    memberNo: '会員番号',
    approvalNo: '稟議番号',
    evaluationSheetK01: '評価シートK01',
    transactionConfirmationK04: '取引確認書K04',
    delegationForm: '通関委任状',
    changeSalesperson: '担当者があなたに変更されますが、進めてよろしいでしょうか。',
    customerGradeUpdated: '顧客レベルの更新に成功しました。',
    promptStatusDone: '「入会手続き完了」を更新したら元に戻せません。\n更新してもよろしいですか？',
    memberLevel: '顧客グレード',
    memberUpgrade: 'グレード更新',
    memberNewLevelPlaceholder: '新しいグレード',
    goBack: 'D2D会員開設申請一覧に戻る',
    roleUpdated: '会員種別を更新しました。',
    editAgency: `${agencyStrings.agency}を変更する`,
    completeMember: 'D2D会員を開設する',
    goMemberDetails: '対応した会員ページへ',
    firstFoodApplication: '初回食品届',
    case: '案件',
    caseEtd: '案件ETD',
    levelOptions: () => {
      const levelOptions = allCompanyLevel.map((value) => ({ value, label: value }));
      return levelOptions;
    },
    completeMemberAlert: () => (
      <>
        「D2D会員」を開設したら元に戻せません。
        <br />
        更新してもよろしいですか？
      </>
    ),
    setRoutePlanListAlert: () => <>代理店を指定してください。</>,
    completeMemberDescription: (no: String) => {
      return (
        <>
          お疲れ様でした！
          <br />
          <span>
            申し込み番号
            {String(no).padStart(9, '0')}
          </span>
          の対応が完了しました。
        </>
      );
    },
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  managementContainer: {
    ...common.flexColumn,
    border: '1px solid #DDDDDD',
  },
  managementCell: {
    ...common.flexColumn,
    padding: '22px 22px 16px',
    '&:not(:last-child)': {
      borderBottom: '1px solid #DDDDDD',
    },
  },
  keyValueRow: {
    ...common.flexInline,
    ...common.middleText,
    color: theme.palette.grey['700'],
    height: 36,
    '& > div': {
      ...common.flexInline,
      alignItems: 'center',
    },
  },
  managementTitle: {
    ...common.middleText,
    ...common.flexInline,
    color: theme.palette.grey['800'],
    fontWeight: 500,
    marginBottom: '0.5rem',
  },
  updatedInfo: {
    ...common.smallerText,
    color: theme.palette.primary.main,
    fontWeight: 500,
    marginTop: '0.1rem',
  },
  managementForm: {
    ...common.flexColumn,
  },
  managementButton: {
    alignSelf: 'center',
    width: 160,
    margin: '0.5rem 0',
  },
  cancelButton: {
    alignSelf: 'center',
    width: 160,
    margin: '0.5rem 0',
    backgroundColor: theme.palette.grey['500'],
    color: theme.palette.primary.contrastText,
  },
  updateButton: {
    alignSelf: 'left',
    width: 60,
    margin: '0.5rem 0',
  },
  inlineButton: {
    height: 32,
    width: 130,
    borderRadius: 16,
  },
  formButton: {
    width: 70,
    height: 32,
    borderRadius: 16,
    margin: '1.5rem 1rem',
  },
  loadingArea: {
    height: '70vh',
    border: '1px solid #DDDDDD',
  },
  inlineForm: {
    flex: 1,
    '& > * + *': {
      marginTop: '0.5rem',
    },
  },
  input: {
    ...common.middleText,
  },
  textInput: {
    padding: 5,
  },
  menuItemRoot: {
    borderColor: theme.palette.grey['800'],
  },
  dialogText: {
    ...common.middleText,
    ...common.fontWeight400,
    ...common.textGray800,
    textAlign: 'center',
    marginTop: 20,
    marginBottom: 60,
    '& span': {
      textDecoration: 'none',
      color: theme.palette.primary.main,
    },
  },
}));

export interface InfoManagementProps {
  id: number;
  type?: 'member' | 'completionRequest';
}

export const InfoManagement: React.FC<InfoManagementProps> = (props) => {
  const { id, type } = props;

  const [showRoleUpdated, setShowRoleUpdated] = useState(false);
  const [memberAgencyDialogOpen, setMemberAgencyDialogOpen] = useState(false);
  const [completeMemberOpen, setCompleteMemberOpen] = useState(false);
  const [showSalesperson, setShowSalesperson] = useState(false);

  const history = useHistory();

  const completeMemberDialog = useConfirmDialog();

  const delegation = useCompanyDelegation(id);
  const companyDetail = useCompanyDetail(id);
  const memberNo = useCompanyMemberNo(id);
  const companyRoutePlanList = useRoutePlanList({
    company_id: id,
  });

  const postCompanySalesperson = usePostCompanySalesperson(id);
  const patchCompanyMemberNo = usePatchCompanyMemberNo(id);
  const patchCompany = usePatchCompany(id);
  const postK01 = usePostK01(id);

  const k01 = useK01(id);

  const user = useUser();

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const isUpdating = [
    postCompanySalesperson.isLoading,
    patchCompanyMemberNo.isLoading,
    patchCompany.isLoading,
    postK01.isLoading,
  ].some(Boolean);

  if (delegation.isLoading || companyDetail.isLoading || memberNo.isLoading) {
    return (
      <>
        <FormTitle title={strings.infoManagement} noEdit />
        <LoadingArea classes={{ container: classes.loadingArea }} />
      </>
    );
  }

  const companyDetailData = companyDetail.data!;
  const memberNoData = memberNo.data!;

  const isSalesperson = user.data!.id === companyDetailData.salesperson_id;
  const isAdmin = user.data?.is_superuser || user.data?.role === 'admin';
  const isFCJapan = user.data?.role === 'FCJapan';

  const memberRoleOptions = [
    {
      value: allCompanyRole[0],
      label: companyStrings.shipper,
    },
    {
      value: allCompanyRole[1],
      label: companyStrings.shipperAgent,
    },
    {
      value: allCompanyRole[2],
      label: companyStrings.consignee,
    },
    {
      value: allCompanyRole[3],
      label: companyStrings.consigneeAgent,
    },
  ];

  const onClickK04 = async () => {
    await downloadUrl({
      url: `/dashboard/company/${id}/k04/`,
      name: 'k04.pdf',
      type: 'application/pdf',
    });
  };

  const onClickK01 = async () => {
    await downloadUrl({
      url: `/dashboard/company/${id}/k01/download/`,
      name: 'k01.xlsx',
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
  };

  const renderKeyValue = (key: string, value: React.ReactNode) => {
    return (
      <div className={classes.keyValueRow}>
        <div style={{ flex: 1 }}>{key}</div>
        <div style={{ flex: 1, letterSpacing: -1 }}>{value}</div>
      </div>
    );
  };

  const renderMemberRole = () => {
    return (
      <Formik
        initialValues={{
          role: companyDetailData.role ?? '',
        }}
        enableReinitialize
        onSubmit={async (values) => {
          try {
            await patchCompany.mutateAsync({
              role: values.role,
            });
            setShowRoleUpdated(true);

            setTimeout(() => setShowRoleUpdated(false), 5000);
          } catch (e) {
            if (e instanceof FormError) {
              // eslint-disable-next-line no-alert
              alert(e.getDisplayMessage());
            }
            throw e;
          }
        }}
      >
        {() => (
          <Form className={classes.managementForm}>
            <div className={classes.managementTitle}>{companyStrings.memberRole}</div>
            <TextInput
              options={memberRoleOptions}
              name="role"
              noLabel
              iconBefore={null}
              choose
              classes={{
                input: classes.input,
                textInput: classes.textInput,
              }}
            />
            <div style={{ height: 10 }} />
            {showRoleUpdated && (
              <>
                <div style={{ width: '1rem' }} />
                <div className={classes.updatedInfo}>{strings.roleUpdated}</div>
              </>
            )}
            <Button
              color="primary"
              shape="round"
              size="small"
              className={classes.updateButton}
              text={formStrings.update}
              type="submit"
              disabled={!isSalesperson && !isAdmin}
            />
          </Form>
        )}
      </Formik>
    );
  };

  const renderAgencies = () => {
    return (
      <div className={classes.managementForm}>
        <MemberAgencyDialog
          id={id}
          open={memberAgencyDialogOpen}
          onClose={() => setMemberAgencyDialogOpen(false)}
          disabled={isFCJapan}
        />

        <div className={classes.managementTitle}>{agencyStrings.agency}</div>
        <Button
          color="primary"
          shape="round"
          size="small"
          text={strings.editAgency}
          className={classes.managementButton}
          type="button"
          onClick={() => setMemberAgencyDialogOpen(true)}
        />
      </div>
    );
  };

  const onClickCompleteMember = async () => {
    if (companyRoutePlanList.data?.length === 0) {
      await completeMemberDialog.confirm({
        title: formStrings.error,
        message: strings.setRoutePlanListAlert(),
      });
      return;
    }

    const result = await completeMemberDialog.confirm({
      title: formStrings.confirm,
      message: strings.completeMemberAlert(),
    });

    if (result === 'cancelled') return;

    await patchCompany.mutateAsync({
      status: '手続き完了',
    });

    setCompleteMemberOpen(true);
  };

  const renderCompleteMemberDialog = () => (
    <Dialog open={completeMemberOpen}>
      <div className={classes.dialogText}>{strings.completeMemberDescription(`${id}`)}</div>
      <br />
      <Button
        color="primary"
        text={strings.goBack}
        style={{ alignSelf: 'center', width: 250 }}
        onClick={() => {
          setCompleteMemberOpen(false);
          history.replace(url('/admin/members/CompletionRequests'));
        }}
      />
      <br />
      <Button
        color="primary"
        text={strings.goMemberDetails}
        style={{ alignSelf: 'center', width: 250 }}
        onClick={() => {
          setCompleteMemberOpen(false);
          history.push(`${url('/admin/members/MemberDetails')}?id=${id}`);
        }}
      />
    </Dialog>
  );

  const renderSalesperson = () => {
    return (
      <>
        {renderKeyValue(strings.assignee, companyDetailData.salesperson_name)}
        {!isSalesperson && (
          <Button
            color="primary"
            shape="round"
            size="small"
            className={classes.managementButton}
            text={strings.becomeAssignee}
            onClick={() => setShowSalesperson(true)}
          />
        )}
        <Dialog open={showSalesperson}>
          <div className={classes.dialogText}>{strings.changeSalesperson}</div>
          <Button
            color="custom"
            shape="round"
            size="small"
            className={classes.cancelButton}
            text={formStrings.cancel}
            onClick={() => setShowSalesperson(false)}
            type="submit"
          />
          <Button
            color="primary"
            shape="round"
            size="small"
            className={classes.managementButton}
            text={formStrings.yes}
            onClick={() => {
              postCompanySalesperson.mutateAsync();
              setShowSalesperson(false);
            }}
            type="submit"
          />
        </Dialog>
      </>
    );
  };

  return (
    <>
      {completeMemberDialog.renderDialog()}
      {renderCompleteMemberDialog()}

      <FormTitle title={strings.infoManagement} noEdit />
      <SpinnerModal open={isUpdating} />
      <div className={classes.managementContainer}>
        <div className={classes.managementCell}>
          {renderKeyValue(strings.memberNo, memberNoData.member_no)}
          {renderKeyValue(strings.approvalNo, memberNoData.approval_no)}
          {renderKeyValue(strings.createdAt, formatDateTime(companyDetailData.created_at))}

          <div style={{ height: 8 }} />
        </div>

        <div className={classes.managementCell}>
          {renderKeyValue(
            strings.firstFoodApplication,
            companyDetail.data?.first_food_application_status,
          )}
          {renderKeyValue(strings.case, companyDetail.data?.first_food_application_d2d_no || '-')}
          {renderKeyValue(strings.caseEtd, companyDetail.data?.first_food_application_etd || '-')}
        </div>

        <div className={classes.managementCell}>
          {renderSalesperson()}
          <div style={{ height: 8 }} />
        </div>

        <div className={classes.managementCell}>
          <div className={classes.managementTitle}>{strings.export}</div>

          <Button
            color="primary"
            shape="round"
            size="small"
            className={classes.managementButton}
            text={strings.evaluationSheetK01}
            onClick={onClickK01}
            disabled={k01.data === undefined}
          />
          <Button
            color="primary"
            shape="round"
            size="small"
            className={classes.managementButton}
            text={strings.transactionConfirmationK04}
            onClick={onClickK04}
          />
          <Button
            color="primary"
            shape="round"
            size="small"
            className={classes.managementButton}
            text={strings.delegationForm}
            disabled={!delegation.data?.file}
            onClick={() => window.open(delegation.data!.file!)}
          />
        </div>

        <div className={classes.managementCell}>{renderMemberRole()}</div>

        <div className={classes.managementCell}>
          <div className={classes.managementForm}>
            <div className={classes.managementTitle}>{companyStrings.customer_service_grades}</div>
            <CompanyCustomerServiceGradesForm />
          </div>
        </div>

        <div className={classes.managementCell}>{renderAgencies()}</div>

        {type === 'completionRequest' && (
          <div className={classes.managementCell}>
            <Button
              color="accent"
              shape="round"
              size="small"
              className={classes.managementButton}
              text={strings.completeMember}
              onClick={onClickCompleteMember}
              disabled={!isAdmin}
            />
          </div>
        )}
      </div>
    </>
  );
};
